import { useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { useAddManualMpzpOrder, useMpzpOrdersForParcel } from "../../hooks";
import { fmtDate } from "../../utils";
import { MpzpStatusBadge } from "./MpzpStatusBadge";

/** Widok, w którym zamawia się wypis i wyrys z systemu — zamówienie obejmujące
 * działkę wiąże się z nią samo (dopasowanie po wierszu albo po numerze). */
export const MPZP_ORDERING_URL = "/power-projects/zamawianie?tab=mpzp";
/** Ta sama biała lista co przy plikach z gminy (backend RESULT_FILE_EXTENSIONS). */
const MANUAL_FILES_ACCEPT = ".pdf,.jpg,.jpeg,.png,.tif,.tiff,.zip,.7z,.rar";

const apiErrorDetail = (e: unknown): string | null => {
  const detail = (
    e as { response?: { data?: { errors?: { detail?: unknown } } } }
  )?.response?.data?.errors?.detail;
  return typeof detail === "string" ? detail : null;
};

/** Sekcja „Wypisy MPZP" w zakładce Status działki: wszystkie zamówienia wypisu
 * i wyrysu z planu miejscowego, w których ta działka występuje — także te, które
 * trzymają ją jako wiersz z innego projektu ZE (dopasowanie po numerze, obrębie
 * i gminie; takie wiersze dostają dopisek).
 *
 * Wypis pozyskany POZA ELGW5 (od inwestora, osobiście w urzędzie) dodaje się stąd
 * ręcznie — samymi plikami. Backend zakłada wtedy zamówienie z flagą
 * `obtained_manually`, od razu „otrzymane", więc wchodzi w tę samą listę i w filtr
 * „Wypis MPZP" jak zamówienia złożone z systemu. */
export function MpzpParcelOrdersSection({ parcelId }: { parcelId: string }) {
  const { data: orders, isLoading } = useMpzpOrdersForParcel(parcelId);
  const addManual = useAddManualMpzpOrder(parcelId);
  const [adding, setAdding] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");

  const closeForm = () => {
    setAdding(false);
    setFiles([]);
    setError("");
  };
  const submit = () => {
    setError("");
    addManual.mutate(files, {
      onSuccess: closeForm,
      onError: (e) =>
        setError(apiErrorDetail(e) ?? "Nie udało się dodać wypisu."),
    });
  };

  if (isLoading) return <Spinner size="sm" />;
  return (
    <div className="space-y-2">
      {!orders?.length ? (
        <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
          Brak zamówień wypisu i wyrysu z MPZP dla tej działki.
        </span>
      ) : (
        <ul className="space-y-1">
          {orders.map((o) => (
            <li
              key={o.id}
              className="flex flex-wrap items-center gap-2 text-[11px] font-mono"
            >
              <Link
                to={`/power-projects/mpzp/${o.id}`}
                className="text-[var(--color-primary)] hover:underline"
              >
                {o.number}
              </Link>
              <MpzpStatusBadge status={o.status} />
              {o.obtained_manually ? (
                <span
                  className="text-[10px] uppercase tracking-wide px-1 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                  title="Wypis pozyskany ręcznie poza ELGW5 — same pliki, bez wniosku, opłat i wysyłki."
                >
                  pozyskany ręcznie
                </span>
              ) : (
                <span className="text-[var(--color-text-secondary)]">
                  {o.scope_label}
                </span>
              )}
              <span className="text-[var(--color-text-muted)] truncate">
                {o.office_name || `gm. ${o.commune}`} · {fmtDate(o.created_at)}
              </span>
              {o.match_type === "number" && (
                <span
                  className="text-[10px] uppercase tracking-wide px-1 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                  title="Zamówienie trzyma tę działkę jako wiersz z innego projektu ZE — dopasowano po numerze, obrębie i gminie."
                >
                  inny projekt
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Skąd bierze się wpis: zamówienie z systemu wiąże się samo, wypis spoza
          ELGW5 trzeba dodać ręcznie — inaczej działka wygląda na „bez wypisu". */}
      <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">
        Wypis i wyrys zamawia się w{" "}
        <Link
          to={MPZP_ORDERING_URL}
          className="text-[var(--color-primary)] hover:underline"
        >
          Zamawianie → Wypisy MPZP
        </Link>{" "}
        — zamówienie obejmujące tę działkę powiąże się tutaj automatycznie.
        Wypis pozyskany poza ELGW5 (np. od inwestora albo osobiście w urzędzie)
        dodaj ręcznie — wystarczą pliki.
      </p>

      {adding ? (
        <div className="space-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
          <div className="flex items-center justify-between gap-2">
            <span className="section-label">
              Wypis pozyskany ręcznie — pliki
            </span>
            <button
              type="button"
              onClick={closeForm}
              title="Anuluj dodawanie"
              aria-label="Anuluj dodawanie"
              className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            >
              <X size={12} />
            </button>
          </div>
          <FileDropZone
            files={files}
            onFilesChange={setFiles}
            multiple
            accept={MANUAL_FILES_ACCEPT}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={files.length === 0}
              loading={addManual.isPending}
              onClick={submit}
            >
              <Upload size={12} /> Dodaj ({files.length})
            </Button>
            <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
              PDF, JPG, PNG, TIFF, ZIP — do 100 MB każdy
            </span>
          </div>
          {error && <div className="text-[11px] text-red-600">{error}</div>}
        </div>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setAdding(true)}
          title="Wypis i wyrys pozyskany poza ELGW5 — powstanie wpis „otrzymany” z samymi plikami, bez wniosku, opłat i wysyłki"
        >
          <FilePlus2 size={12} /> Dodaj pozyskany ręcznie
        </Button>
      )}
    </div>
  );
}
